import * as React from 'react';
import Media from 'react-media';

import debounce from 'lodash/debounce';

import connectToStores from 'js/lib/connectToStores';
import type UserAgentInfo from 'js/lib/useragent';

import type { StyleDeclarationValue } from '@coursera/aphrodite';
import { StyleSheet, breakPoint, css } from '@coursera/coursera-ui';

import { BASE_STYLES } from 'bundles/design-system/components/container-styles';
import pageVariables from 'bundles/page/components/__styles__/pageVariables.json';
import ApplicationStoreClass from 'bundles/ssr/stores/ApplicationStore';

const styles = StyleSheet.create({
  banner: {
    ...BASE_STYLES.containerWrapper,
    position: 'fixed',
    zIndex: '3000',
    width: '100%',
  },
  hidden: {
    display: 'none',
  },
  desktopBanner: {
    top: '0',
    minHeight: pageVariables.header.height, // px
    paddingTop: '8px',
    paddingBottom: '8px',
    borderBottom: '1px solid #D8D8D8',
    background: '#F8F8F8',
  },
  mobileBanner: {
    bottom: '0',
    borderTop: '1px solid #E1E1E1',
    background: 'white',
    maxHeight: '4rem',
    height: '100%',
    '@supports (padding-bottom: constant(safe-area-inset-bottom))': {
      paddingBottom: 'constant(safe-area-inset-bottom)', // iOS 11
    },
    '@supports (padding-bottom: env(safe-area-inset-bottom))': {
      paddingBottom: 'env(safe-area-inset-bottom)', // iOS 11.2+
    },
  },
});

type PropsFromCaller = {
  children: (isMobile: boolean) => JSX.Element;
  shouldShowStickyBanner: (isMobile: boolean) => boolean;
  bannerStyles?: StyleDeclarationValue;
  mobileBreakPoint?: number;
};

type PropsFromStore = {
  userAgent?: UserAgentInfo;
};

type PropsToComponent = PropsFromCaller & PropsFromStore;

type State = {
  isStickyBannerShown: boolean;
  isMobile: boolean;
  componentDidMount: boolean;
};

class StickyBanner extends React.Component<PropsToComponent, State> {
  static defaultProps = {
    mobileBreakPoint: breakPoint.sm,
  };

  constructor(props: PropsToComponent) {
    super(props);
    this.state = {
      isStickyBannerShown: false,
      isMobile: false,
      componentDidMount: false,
    };
  }

  componentDidMount() {
    this.setState(() => ({ componentDidMount: true }), this.checkStickyBannerVisibility);
    window.addEventListener('scroll', this.onResizeOrScroll);
    window.addEventListener('resize', this.onResizeOrScroll);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.onResizeOrScroll);
    window.removeEventListener('resize', this.onResizeOrScroll);
  }

  onMediaChange = (isMobile: boolean) => {
    this.setState(() => ({ isMobile }));
  };

  // This can't be placed after onResizeOrScroll, will cause TS compilation error
  checkStickyBannerVisibility = () => {
    const { shouldShowStickyBanner } = this.props;
    const { isStickyBannerShown, isMobile } = this.state;

    if (shouldShowStickyBanner(isMobile) && !isStickyBannerShown) {
      this.setState(() => ({ isStickyBannerShown: true }));
    } else if (!shouldShowStickyBanner(isMobile) && isStickyBannerShown) {
      this.setState(() => ({ isStickyBannerShown: false }));
    }
  };

  onResizeOrScroll = debounce(this.checkStickyBannerVisibility, 10);

  render() {
    const { children, bannerStyles, mobileBreakPoint, userAgent } = this.props;
    const { componentDidMount, isStickyBannerShown } = this.state;
    return (
      <div className="rc-StickyBanner">
        {componentDidMount && (
          <Media
            query={{ maxWidth: mobileBreakPoint! }}
            defaultMatches={userAgent?.isMobileBrowser}
            onChange={this.onMediaChange}
          >
            {(isMobile) => {
              const breakPointStyles = isMobile ? styles.mobileBanner : styles.desktopBanner;

              return (
                <div {...css(bannerStyles, styles.banner, isStickyBannerShown ? breakPointStyles : styles.hidden)}>
                  {children(isMobile)}
                </div>
              );
            }}
          </Media>
        )}
      </div>
    );
  }
}

export default connectToStores<PropsToComponent, PropsFromCaller>(
  [ApplicationStoreClass],
  (ApplicationStore, props) => ({
    ...props,
    userAgent: ApplicationStore.getUserAgent(),
  })
)(StickyBanner);
